import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  useTheme,
  CssBaseline,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Chip,
  Stack,
  useMediaQuery,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TablePagination,
  InputAdornment,
  Tab,
  Tabs,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  ApplicationType,
  date,
  entity_id,
  getGeolocation,
  location,
  locationGeo,
  orderID,
  Platform,
} from "../Utils/Const";
import { baseapi } from "../Environmentalurl";
import { useNavigate } from "react-router-dom";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function MobileRecharge() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const md = useMediaQuery("(min-width:900px)");

  const [subscriber, setSubscriber] = React.useState("");
  const [rechargeType, setRechargeType] = useState("Prepaid");
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [tpinOpen, setTpinOpen] = useState(false);
  const [tpin, setTpin] = useState("");
  const [isVerifyingTPIN, setIsVerifyingTPIN] = useState(false);

  // Tab state
  const [selectedPlanTab, setSelectedPlanTab] = useState("All");

  // NEW: operator / biller / amounts / UI helper states
  const [currentOperator, setCurrentOperator] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [currentOptBillerId, setCurrentOptBillerId] = useState("");
  const [amount, setAmount] = useState("");
  const [modalAmount, setModalAmount] = useState("");
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [lastPlanResponse, setLastPlanResponse] = useState(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientMsg, setInsufficientMsg] = useState("");
  const [selectedPlanAmount, setSelectedPlanAmount] = useState("");
  const inputRefs = useRef([]);
  const ip = useIPAddress();

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Popups
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMsg, setPopupMsg] = useState("");

  // Payment Confirmation Popup
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // ── Charges summary (BBPS GetCharges) ──────────────────────────────────
  const [chargesLoading, setChargesLoading] = useState(false);
  const [chargesData, setChargesData] = useState(null);
  const [chargesOpen, setChargesOpen] = useState(false);

  const navigate = useNavigate();

  // ---------- Derive unique plan categories from response ----------
  const planCategories = useMemo(() => {
    const cats = [...new Set(plans.map((p) => p.planName))];
    return ["All", ...cats];
  }, [plans]);

  // Reset tab & page when plans change
  useEffect(() => {
    setSelectedPlanTab("All");
    setPage(0);
  }, [plans]);

  // Reset page when tab or search changes
  useEffect(() => {
    setPage(0);
  }, [selectedPlanTab, search]);

  const handleProceed = async () => {
    try {
      if (!mobileNumber || mobileNumber.length !== 10) {
        setPopupMsg("Please enter a valid 10-digit mobile number!");
        setPopupOpen(true);
        return;
      }

      const sessionid = sessionStorage.getItem("SessionID");

      if (!sessionid) {
        setPopupMsg("Session ID missing!");
        setPopupOpen(true);
        return;
      }

      const requestBody = {
        ApplicationType,
        GeoLocation: locationGeo,
        Mobilenumber: mobileNumber,
        Platform,
        ReqType: "Jan_Aadhaar_eWallet_BBPSMobilePlan",
        SessionID: sessionid,
        MachineID: ip,
        EntityID: entity_id,
        Date: date,
      };

      const response = await api.post(
        `${baseapi}/janAadhaareWalletBBPSMobilePlan`,
        requestBody,
      );

      const res = response.data;
      console.log("plan response:", res);

      if (res.Response_Code !== "1000") {
        setPopupMsg(res.Message || "Something went wrong!");
        setPopupOpen(true);
        return;
      }

      setPlans(res.Browsing_plans || []);
      setLastPlanResponse(res);

      // AUTO-SET operator from API
      if (res.MNP_Details && res.MNP_Details.length > 0) {
        const mnp = res.MNP_Details[0];
        setSelectedOperator({
          label: mnp.currentOperator,
          value: mnp.currentOperator,
        });
        setCurrentOperator(mnp.currentOperator);
        setCurrentOptBillerId(mnp.currentOptBillerId);
        setCurrentLocation(mnp.currentLocation || "");
      }
    } catch (error) {
      console.log("handleProceed error:", error);
      setPopupMsg("Server Error! Try again later.");
      setPopupOpen(true);
    }
  };

  // ---------- Filter by tab + search ----------
  const filteredPlans = useMemo(() => {
    let result = plans;

    // Filter by selected plan category tab
    if (selectedPlanTab !== "All") {
      result = result.filter((p) => p.planName === selectedPlanTab);
    }

    // Filter by search text
    if (search.trim()) {
      result = result.filter((p) =>
        Object.values(p).some((value) =>
          String(value).toLowerCase().includes(search.toLowerCase()),
        ),
      );
    }

    return result;
  }, [plans, search, selectedPlanTab]);

  // ---------- Get BBPS Charges → show summary ----------
  const handleGetCharges = async () => {
    if (!selectedPlan) {
      setPopupMsg("No plan selected!");
      setPopupOpen(true);
      return;
    }

    setConfirmOpen(false);
    setChargesLoading(true);

    try {
      const mobile = sessionStorage.getItem("mobileNumber");
      const session = sessionStorage.getItem("SessionID");
      const billerId =
        currentOptBillerId ||
        (lastPlanResponse?.MNP_Details?.[0]?.currentOptBillerId ?? "");

      const chargesRes = await api.post(`${baseapi}/getbbpscharges`, {
        ReqType: "TA_BBPS_GetCharges",
        TrxnAmount: String(selectedPlan.amount || "0"),
        BillerID: billerId,
        EntityID: entity_id,
        MachineID: ip,
        ApplicationType: ApplicationType,
        Platform: Platform,
        GeoLocation: locationGeo,
        SessionID: session,
        Mobilenumber: mobile,
        ServiceID: "3",
      });

      const resData = chargesRes.data;

      if (resData?.Response_Code !== "1000" || !resData?.TotalPayable) {
        setPopupMsg(
          resData?.Message || "Unable to calculate charges. Please try again.",
        );
        setPopupOpen(true);
        return;
      }

      setChargesData(resData);
      setChargesOpen(true);
    } catch (error) {
      console.log("getbbpscharges error:", error);
      setPopupMsg("Unable to calculate charges. Please try again.");
      setPopupOpen(true);
    } finally {
      setChargesLoading(false);
    }
  };

  // ── Charges confirmed → check wallet balance ─────────────────────────────
  const handleConfirmCharges = () => {
    setChargesOpen(false);
    validateBalanceAndProceed();
  };

  // ---------- Balance check ----------
  const validateBalanceAndProceed = async () => {
    try {
      const requestBody = {
        ApplicationType: ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        Mobilenumber: sessionStorage.getItem("mobileNumber"),
        Platform: Platform,
        WalletTypeID: "1",
        ReqType: "Jan_Aadhaar_eWallet_Statement",
        SessionID: sessionStorage.getItem("SessionID"),
        MachineID: ip,
        FromDate: "",
        ToDate: "",
      };

      const response = await api.post(
        `${baseapi}/JanAadhaareWalletStatement`,
        requestBody,
      );

      console.log("statement response:", response.data);

      const { Info } = response.data;
      if (!Info || Info.length === 0) {
        setPopupMsg("Unable to read wallet info.");
        setPopupOpen(true);
        return;
      }
      const walletBalance = parseFloat(Info[0].Balance);
      const rechargeAmount = parseFloat(
        chargesData?.TotalPayable ?? selectedPlan.amount,
      );

      setSelectedPlanAmount(String(rechargeAmount));

      if (walletBalance < rechargeAmount) {
        setInsufficientMsg(
          `Insufficient wallet balance. Amount payable is ₹${rechargeAmount}, but your wallet balance is ₹${walletBalance}.`,
        );
        setInsufficientOpen(true);
        return;
      }

      setTpinOpen(true);
    } catch (error) {
      console.log("validateBalance error:", error);
      setPopupMsg("Unable to check wallet balance!");
      setPopupOpen(true);
    }
  };

  // ---------- TPIN verification ----------
  const verifyTPIN = async () => {
    if (!tpin.trim()) {
      setPopupMsg("Please enter TPIN");
      setPopupOpen(true);
      return;
    }

    const mobileFromSession = sessionStorage.getItem("mobileNumber");
    const sessionid = sessionStorage.getItem("SessionID");

    if (!mobileFromSession || !sessionid) {
      setPopupMsg("Missing Mobile Number or Session ID");
      setPopupOpen(true);
      return;
    }

    setIsVerifyingTPIN(true);

    const body = {
      ReqType: "MT_TPIN_Management",
      Mobilenumber: mobileFromSession,
      Statement: "ValidateTPIN",
      TPIN: String(tpin),
      OldTPIN: "",
      Platform: String(Platform),
      EntityID: String(entity_id),
      GeoLocation: locationGeo,
      MachineID: ip,
    };

    try {
      const res = await api.post(`${baseapi}/tpin`, body);
      console.log("TPIN RESPONSE:", res.data);

      if (res.data.Response_Code === "1692") {
        setTpinOpen(false);
        await processPaymentAfterTPIN();
      } else {
        setPopupMsg(res.data.Message || "Incorrect TPIN");
        setPopupOpen(true);
      }
    } catch (error) {
      console.log("AXIOS ERROR verifyTPIN:", error);
      setPopupMsg("Server error! Please try again.");
      setPopupOpen(true);
    } finally {
      setIsVerifyingTPIN(false);
    }
  };

  // ---------- Payment API call ----------
  const processPaymentAfterTPIN = async () => {
    try {
      if (!selectedPlan) {
        setPopupMsg("No plan selected for payment");
        setPopupOpen(true);
        return;
      }

      let paramInfo = currentOperator || "";
      let spKey = currentOptBillerId || "";
      let locationFromMnp = currentLocation || "";

      if (lastPlanResponse && lastPlanResponse.MNP_Details?.length > 0) {
        const mnp = lastPlanResponse.MNP_Details[0];
        paramInfo = mnp.currentOperator || paramInfo;
        spKey = mnp.currentOptBillerId || spKey;
        locationFromMnp = mnp.currentLocation || locationFromMnp;
      }

      const mobileFromSession = sessionStorage.getItem("mobileNumber") || "";
      const payableAmount = parseFloat(
        chargesData?.TotalPayable ?? selectedPlan.amount,
      );

      const body = {
        ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
        MachineID: ip,
        Mobilenumber: mobileFromSession,
        SessionID: sessionStorage.getItem("SessionID"),
        Date: date,
        OrderId: orderID,
        TPIN: tpin,
        RequestType: "BA_NoNBBPS_Payment",
        ParameterInfo: paramInfo,
        IsExternalTransaction: "",
        Account_num: mobileFromSession,
        CustomerID: mobileFromSession,
        SpKey: spKey,
        Amount: payableAmount,
        Optional1: ["Mobile Number", "Location"],
        Optional2: [mobileNumber, locationFromMnp],
        Optional3: true,
        Optional4: "",
        Optional5: "PGFtb3VudFRhZ3MPC9hbW91bnRUYWdzPg==",
        Optional6: "OPTIONAL",
        Optional7: "",
        IsBillFetch: "",
        Platform: Platform,
        ApplicationType: ApplicationType,
        EntityID: entity_id,
        GeoLocation: location,
        Statement: "ProcessDebit",
        ServiceID: "3",
        Narration: `Payment of Rs. ${payableAmount} to ${paramInfo}`,
      };

      console.log("Payment request body:", body);

      const res = await api.post(
        `${baseapi}/JanAadhaareWalletBBPSPayments`,
        body,
      );

      console.log("Payment response:", res.data);

      if (
        res.data.Response_Code === "1000" ||
        res.data.Response_Code === "100"
      ) {
        alert("Payment successful!");
        window.location.href = "/dashboard";
      } else {
        setPopupMsg(res.data.Message || "Payment failed");
        setPopupOpen(true);
      }
    } catch (error) {
      console.log("processPaymentAfterTPIN error:", error);
      setPopupMsg("Payment failed due to server error");
      setPopupOpen(true);
    }
  };

  return (
    <>
      {/* Error Popup */}
      <Dialog open={popupOpen} onClose={() => setPopupOpen(false)}>
        <DialogTitle>Notice</DialogTitle>
        <DialogContent>{popupMsg}</DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Payment Popup */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to recharge <b>{mobileNumber}</b> with{" "}
            <b>₹{selectedPlan?.amount}</b>?
          </Typography>
          {selectedPlan && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                background: isDark ? "#1e293b" : "#f0fdf4",
                borderRadius: 2,
                border: isDark ? "1px solid #334155" : "1px solid #bbf7d0",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <b>Plan:</b> {selectedPlan.planName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>Validity:</b> {selectedPlan.validity || "N/A"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                <b>Details:</b> {selectedPlan.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#114c28", color: "white" }}
            disabled={chargesLoading}
            onClick={handleGetCharges}
          >
            {chargesLoading ? "Please wait..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CHARGES SUMMARY DIALOG ──────────────────────────────── */}
      <Dialog
        open={chargesOpen}
        onClose={() => setChargesOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(90deg, #114c28 0%, #22c55e 100%)",
            py: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -20,
              right: -20,
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
            Payment Summary
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>
            Review charges before proceeding
          </Typography>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {chargesData && (
            <Box sx={{ bgcolor: "#f5f7fb", borderRadius: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1.2}>
                <Typography color="text.secondary" fontSize={14}>
                  Transaction Amount
                </Typography>
                <Typography fontWeight={600}>
                  ₹{chargesData.TransactionAmount}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1.2}>
                <Typography color="text.secondary" fontSize={14}>
                  Charges
                </Typography>
                <Typography fontWeight={600}>₹{chargesData.Charges}</Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                mb={1.2}
                pb={1.5}
                sx={{ borderBottom: "1px dashed #ccc" }}
              >
                <Typography color="text.secondary" fontSize={14}>
                  GST
                </Typography>
                <Typography fontWeight={600}>₹{chargesData.GST}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1.5}>
                <Typography fontWeight={700} fontSize={16}>
                  Total Payable
                </Typography>
                <Typography
                  fontWeight={700}
                  fontSize={16}
                  sx={{ color: "#114c28" }}
                >
                  ₹{chargesData.TotalPayable}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setChargesOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleConfirmCharges}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 700,
              background: "linear-gradient(90deg, #114c28 0%, #22c55e 100%)",
              boxShadow: "0 6px 16px rgba(17,76,40,0.3)",
            }}
          >
            Pay Now ₹{chargesData?.TotalPayable}
          </Button>
        </DialogActions>
      </Dialog>

      {/* INSUFFICIENT BALANCE DIALOG */}
      <Dialog
        open={insufficientOpen}
        onClose={() => setInsufficientOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            width: 380,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
            fontSize: 18,
            pb: 0.5,
          }}
        >
          ⚠️ Insufficient Balance
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", mt: 1 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {insufficientMsg}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setInsufficientOpen(false)}
            fullWidth
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setInsufficientOpen(false);
              navigate("/dashboard/loadmoney");
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              ml: 1,
              backgroundColor: "#114c28",
              color: "white",
              "&:hover": { backgroundColor: "#0d3d20" },
            }}
          >
            Load Money
          </Button>
        </DialogActions>
      </Dialog>

      {/* TPIN Dialog */}
      <Dialog
        open={tpinOpen}
        onClose={() => setTpinOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "6px",
            width: "380px",
            backgroundColor: isDark ? "#1d2633" : "#fefefe",
            boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "20px",
            textAlign: "center",
            color: isDark ? "#fff" : "#114c28",
            pb: 1,
          }}
        >
          🔐 Enter TPIN
        </DialogTitle>
        <DialogContent
          sx={{
            px: 4,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            {[0, 1, 2, 3].map((index) => (
              <TextField
                key={index}
                type="password"
                value={tpin[index] || ""}
                inputRef={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(-1);

                  const newTpin = tpin.padEnd(4, "").split("");
                  newTpin[index] = value;

                  setTpin(newTpin.join(""));

                  // Move to next box automatically
                  if (value && index < 3) {
                    inputRefs.current[index + 1]?.focus();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !tpin[index] && index > 0) {
                    inputRefs.current[index - 1]?.focus();
                  }
                }}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: 600,
                  },
                }}
                sx={{
                  width: 60,
                  "& .MuiOutlinedInput-root": {
                    height: 60,
                    borderRadius: 3,
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setTpinOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isVerifyingTPIN}
            onClick={verifyTPIN}
            sx={{
              backgroundColor: "#114c28",
              color: "white",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "10px",
              px: 4,
              "&:hover": { backgroundColor: "#0d3d20" },
            }}
          >
            {isVerifyingTPIN ? "Verifying..." : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>

      <CssBaseline />
      <Box
        sx={{
          background: "#f8fafc",
          px: { xs: 2, md: 6 },
          transition: "background 0.4s ease",
        }}
      >
        <Grid container spacing={3}>
          {/* ── LEFT CARD ── */}
          <Grid size={{ xs: 12, md: 4 }} item xs={12} md={4}>
            <Card
              elevation={isDark ? 0 : 2}
              sx={{
                background: "white",
                borderRadius: 3,
                px: { md: 3 },
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                transition: "all 0.3s ease",
                border: "1px solid #f0f0f0",
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: 15 },
                  }}
                >
                  Recharge Mobile
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Enter your mobile number to proceed
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Mobile Number
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="off"
                  placeholder="Enter Mobile Number"
                  variant="filled"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  inputProps={{ maxLength: 10 }}
                  sx={{
                    mt: 1,
                    mb: 2,
                    "& .MuiFilledInput-root": {
                      backgroundColor: "#f8fafc",
                      borderRadius: 1,
                    },
                  }}
                />

                <Button
                  fullWidth
                  size="large"
                  onClick={handleProceed}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #16a34a, #22c55e)",
                    color: "#fff",
                    borderRadius: 2,
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { filter: "brightness(1.05)" },
                  }}
                >
                  Proceed to Recharge
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 3,
                    color: "text.secondary",
                  }}
                >
                  <ShieldOutlinedIcon
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography variant="body2">
                    100% Secure & Instant Recharge
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ── RIGHT CARD ── */}
          <Grid size={{ xs: 12, md: 8 }} item xs={12} md={8}>
            <Card
              elevation={isDark ? 0 : 2}
              sx={{
                background: "white",
                borderRadius: 3,
                p: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                height: "100%",
                transition: "all 0.3s ease",
                border: "1px solid #f0f0f0",
              }}
            >
              <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      background: isDark
                        ? "rgba(35,190,165,0.12)"
                        : "rgba(0,123,255,0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  >
                    <SearchIcon />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ color: "text.primary", fontWeight: 700 }}
                  >
                    Browse Plans
                  </Typography>
                </Box>

                {plans.length > 0 ? (
                  <>
                    {/* ── Operator + Location header ── */}
                    <Box
                      sx={{
                        mt: 2,
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color="text.primary"
                      >
                        Browse Plans of{" "}
                        <span style={{ color: theme.palette.primary.main }}>
                          {currentOperator}
                        </span>
                        {currentLocation ? ` - ${currentLocation}` : ""}
                      </Typography>

                      {/* Search box */}
                      <TextField
                        size="small"
                        autoComplete="off"
                        placeholder="Search plans…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          minWidth: 200,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: isDark ? "#12303a" : "#f5f5f5",
                          },
                        }}
                      />
                    </Box>

                    {/* ── Plan Category Tabs ── */}
                    <Box
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        mb: 2,
                        overflowX: "auto",
                        "&::-webkit-scrollbar": { height: "4px" },
                        "&::-webkit-scrollbar-thumb": {
                          background: isDark ? "#334155" : "#cbd5e1",
                          borderRadius: 4,
                        },
                      }}
                    >
                      <Tabs
                        value={selectedPlanTab}
                        onChange={(e, val) => setSelectedPlanTab(val)}
                        variant="scrollable"
                        scrollButtons="auto"
                        TabIndicatorProps={{
                          style: {
                            backgroundColor: theme.palette.primary.main,
                            height: 3,
                            borderRadius: 2,
                          },
                        }}
                        sx={{
                          minHeight: 40,
                          "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: { xs: 11, sm: 13 },
                            minHeight: 40,
                            py: 0.5,
                            px: { xs: 1.5, sm: 2 },
                            color: isDark ? "#94a3b8" : "#64748b",
                            whiteSpace: "nowrap",
                            "&.Mui-selected": {
                              color: theme.palette.primary.main,
                            },
                          },
                        }}
                      >
                        {planCategories.map((cat) => (
                          <Tab
                            key={cat}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.8,
                                }}
                              >
                                {cat}
                                {cat !== "All" && (
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      backgroundColor:
                                        selectedPlanTab === cat
                                          ? theme.palette.primary.main
                                          : isDark
                                            ? "#334155"
                                            : "#e2e8f0",
                                      color:
                                        selectedPlanTab === cat
                                          ? "#fff"
                                          : isDark
                                            ? "#94a3b8"
                                            : "#64748b",
                                      borderRadius: "10px",
                                      px: 0.8,
                                      py: 0.2,
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {
                                      plans.filter((p) => p.planName === cat)
                                        .length
                                    }
                                  </Box>
                                )}
                              </Box>
                            }
                            value={cat}
                          />
                        ))}
                      </Tabs>
                    </Box>

                    {/* ── Plans Table ── */}
                    {filteredPlans.length > 0 ? (
                      <>
                        <TableContainer
                          component={Paper}
                          sx={{
                            borderRadius: 3,
                            boxShadow: isDark
                              ? "0px 2px 10px rgba(255,255,255,0.08)"
                              : "0px 2px 10px rgba(0,0,0,0.1)",
                            overflowX: "auto",
                            "&::-webkit-scrollbar": { height: "6px" },
                            "&::-webkit-scrollbar-thumb": {
                              background: isDark ? "#334155" : "#cbd5e1",
                              borderRadius: 4,
                            },
                          }}
                        >
                          <Table
                            sx={{ minWidth: { xs: 600, sm: 800, md: 900 } }}
                          >
                            <TableHead>
                              <TableRow
                                sx={{
                                  background: isDark
                                    ? "linear-gradient(90deg, #1e293b 0%, #334155 100%)"
                                    : "linear-gradient(90deg, #e8f5e9 0%, #c8e6c9 100%)",
                                }}
                              >
                                {[
                                  "Plan",
                                  "Operator",
                                  "Validity",
                                  "Talktime",
                                  "Description",
                                  "Price",
                                  "Action",
                                ].map((h) => (
                                  <TableCell
                                    key={h}
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: {
                                        xs: "10px",
                                        sm: "12px",
                                        md: "13px",
                                      },
                                      color: isDark ? "#d1e3f8" : "#15803d",
                                      textTransform: "uppercase",
                                      letterSpacing: 0.4,
                                      borderBottom: "none",
                                      py: 1.5,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {h}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {filteredPlans
                                .slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage,
                                )
                                .map((p, i) => (
                                  <TableRow
                                    key={i}
                                    sx={{
                                      backgroundColor: isDark
                                        ? i % 2 === 0
                                          ? "#0f172a"
                                          : "#1e293b"
                                        : i % 2 === 0
                                          ? "#ffffff"
                                          : "#f8fdf8",
                                      transition: "all 0.25s ease",
                                      "&:hover": {
                                        transform: "scale(1.005)",
                                        backgroundColor: isDark
                                          ? "#26344d"
                                          : "#e8f5e9",
                                        boxShadow: isDark
                                          ? "0px 0px 6px rgba(255,255,255,0.1)"
                                          : "0px 0px 6px rgba(0,0,0,0.08)",
                                      },
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        fontSize: {
                                          xs: "10px",
                                          sm: "12px",
                                          md: "13px",
                                        },
                                      }}
                                    >
                                      <Chip
                                        label={p.planName}
                                        size="small"
                                        sx={{
                                          fontSize: { xs: "9px", sm: "11px" },
                                          fontWeight: 600,
                                          backgroundColor: isDark
                                            ? "rgba(35,190,165,0.15)"
                                            : "rgba(0,123,255,0.08)",
                                          color: theme.palette.primary.main,
                                          border: `1px solid ${theme.palette.primary.main}30`,
                                        }}
                                      />
                                    </TableCell>

                                    <TableCell
                                      sx={{
                                        fontSize: {
                                          xs: "10px",
                                          sm: "12px",
                                          md: "13px",
                                        },
                                      }}
                                    >
                                      {p.serviceProviderName}
                                    </TableCell>

                                    <TableCell
                                      sx={{
                                        fontSize: {
                                          xs: "10px",
                                          sm: "12px",
                                          md: "13px",
                                        },
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {p.validity || "N/A"}
                                    </TableCell>

                                    <TableCell
                                      sx={{
                                        fontSize: {
                                          xs: "10px",
                                          sm: "12px",
                                          md: "13px",
                                        },
                                      }}
                                    >
                                      {parseFloat(p.talktime) > 0
                                        ? `₹${p.talktime}`
                                        : "—"}
                                    </TableCell>

                                    <TableCell sx={{ maxWidth: 260 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          fontSize: {
                                            xs: "10px",
                                            sm: "11px",
                                            md: "12px",
                                          },
                                          color: "text.secondary",
                                        }}
                                        title={p.description}
                                      >
                                        {p.description}
                                      </Typography>
                                    </TableCell>

                                    <TableCell>
                                      <Box
                                        sx={{
                                          backgroundColor: "#15803d",
                                          color: "white",
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: "30px",
                                          textAlign: "center",
                                          fontWeight: 700,
                                          fontSize: {
                                            xs: "10px",
                                            sm: "12px",
                                            md: "13px",
                                          },
                                          minWidth: 55,
                                          display: "inline-block",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        ₹{p.amount}
                                      </Box>
                                    </TableCell>

                                    <TableCell>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          color: "white",
                                          background:
                                            "linear-gradient(90deg, #2e7d32, #43a047)",
                                          textTransform: "none",
                                          py: 0.6,
                                          px: 1.5,
                                          borderRadius: "25px",
                                          fontWeight: 600,
                                          fontSize: { xs: "10px", sm: "12px" },
                                          boxShadow: "none",
                                          whiteSpace: "nowrap",
                                          "&:hover": {
                                            background:
                                              "linear-gradient(90deg, #43a047, #2e7d32)",
                                            boxShadow:
                                              "0 2px 8px rgba(0,0,0,0.2)",
                                          },
                                        }}
                                        onClick={() => {
                                          setSelectedPlan(p);
                                          setAmount(String(p.amount));
                                          setModalAmount(String(p.amount));
                                          setSelectedPlanAmount(
                                            String(p.amount),
                                          );
                                          setChargesData(null);

                                          if (
                                            lastPlanResponse?.MNP_Details
                                              ?.length > 0
                                          ) {
                                            const mnp =
                                              lastPlanResponse.MNP_Details[0];
                                            setSelectedOperator({
                                              label: mnp.currentOperator || "",
                                              value: mnp.currentOperator || "",
                                            });
                                            setCurrentOperator(
                                              mnp.currentOperator || "",
                                            );
                                            setCurrentOptBillerId(
                                              mnp.currentOptBillerId || "",
                                            );
                                            setCurrentLocation(
                                              mnp.currentLocation || "",
                                            );
                                          } else {
                                            setSelectedOperator({
                                              label:
                                                p.serviceProviderName || "",
                                              value:
                                                p.serviceProviderName || "",
                                            });
                                            setCurrentOperator(
                                              p.serviceProviderName || "",
                                            );
                                          }

                                          setConfirmOpen(true);
                                        }}
                                      >
                                        Proceed
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            mt: 2,
                            px: 1,
                            py: 1,
                            background: isDark ? "#1e293b" : "#f1f8f6",
                            borderRadius: 2,
                          }}
                        >
                          <TablePagination
                            component="div"
                            count={filteredPlans.length}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                              setRowsPerPage(parseInt(e.target.value, 10));
                              setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            sx={{
                              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                                {
                                  color: isDark ? "#d1e3f8" : "#15803d",
                                  fontWeight: 600,
                                },
                              ".MuiTablePagination-actions button": {
                                color: isDark ? "#d1e3f8" : "#15803d",
                              },
                              ".MuiInputBase-root": {
                                borderRadius: 1.5,
                                background: isDark ? "#0f172a" : "#fff",
                                px: 1,
                                fontWeight: 600,
                              },
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          background: isDark ? "#12303a" : "rgba(0,0,0,0.04)",
                          borderRadius: 3,
                          p: 4,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        <Typography variant="body1" fontWeight={500}>
                          No plans found matching your search.
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, mb: 2 }}
                    >
                      Select an operator to see available plans
                    </Typography>
                    <Box
                      sx={{
                        background: isDark ? "#12303a" : "rgba(0,0,0,0.05)",
                        borderRadius: 2,
                        p: 3,
                        color: "text.secondary",
                        mt: 1,
                        mb: 2,
                        minHeight: 92,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography>
                        Enter your mobile number and click "Proceed to Recharge"
                        to load plans.
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Disclaimer */}
                <Box
                  sx={{
                    background: isDark ? "#0f3a35" : "rgba(0,123,255,0.05)",
                    borderRadius: 1.5,
                    p: 1.2,
                    color: "text.secondary",
                    mt: 2,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    Disclaimer:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    While we support most recharges, please verify with your
                    operator before proceeding.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Trusted Section */}
          <Grid size={{ xs: 12 }} item xs={12}>
            <Box
              sx={{
                borderRadius: md ? 2 : 0,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary">
                Trusted by <b>UIDAI BBPS Network</b>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}